import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaSave, FaTag, FaBox, FaGlobe, FaHandshake, FaUserTie } from 'react-icons/fa';
import { BsX } from 'react-icons/bs';

const PartnerPricing = ({ productId, partnerId }) => {
    const [product, setProduct] = useState(null);
    const [pricings, setPricings] = useState([]);
    const [overrides, setOverrides] = useState([]);
    const [editingRows, setEditingRows] = useState({});
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);
    const [filterProduct, setFilterProduct] = useState('');
    const [filterLicense, setFilterLicense] = useState('');

    const getAuthHeaders = () => {
        const userInfo = JSON.parse(localStorage.getItem('user-info'));
        const origin = localStorage.getItem('user-origin');
        return {
            Authorization: `Bearer ${userInfo?.token}`,
            'Tenant-Origin': origin,
        };
    };

    const fetchProductDetails = useCallback(async () => {
        try {
            setLoading(true);
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const headers = getAuthHeaders();

            // 1. Fetch Basic Product Info
            let basePricings = [];
            if (productId) {
                const response = await axios.get(`${backendUrl}/api/products/${productId}`, {
                    headers,
                    skipLoader: true,
                });
                const data = response.data?.Data || response.data?.data;
                setProduct(data);
                basePricings = data?.pricings || [];
            } else {
                const response = await axios.get(`${backendUrl}/api/products`, {
                    headers,
                    skipLoader: true,
                });
                const productsList = response.data?.Data || response.data?.data || [];
                productsList.forEach(prod => {
                    if (prod.pricings && prod.pricings.length > 0) {
                        prod.pricings.forEach(pricing => {
                            basePricings.push({
                                ...pricing,
                                product_name: prod.product_name || prod.name
                            });
                        });
                    }
                });
            }

            // 2. Fetch Overrides if partnerId exists
            if (partnerId) {
                const overrideRes = await axios.get(`${backendUrl}/api/partner-product-pricing`, {
                    headers,
                    params: { partner_id: partnerId },
                    skipLoader: true
                });
                const clientOverrides = overrideRes.data?.data || [];

                // Merge overrides into basePricings
                basePricings = basePricings.map(bp => {
                    const match = clientOverrides.find(ov => ov.product_pricing_id === bp.id);
                    if (match) {
                        return {
                            ...bp,
                            global_partner_price: bp.partner_price,
                            global_end_customer_price: bp.end_customer_price,
                            partner_price: match.partner_price,
                            end_customer_price: match.end_customer_price,
                            override_id: match.id
                        };
                    } else {
                        return {
                            ...bp,
                            global_partner_price: bp.partner_price,
                            global_end_customer_price: bp.end_customer_price,
                            partner_price: null,
                            end_customer_price: null,
                            override_id: null
                        };
                    }
                });
            }

            setPricings(basePricings);
        } catch (error) {
            console.error('Error fetching product pricing details:', error);
        } finally {
            setLoading(false);
        }
    }, [productId, partnerId]);

    useEffect(() => {
        fetchProductDetails();
    }, [fetchProductDetails]);

    // Reset editing state when partner or product changes
    useEffect(() => {
        setEditingRows({});
    }, [partnerId, productId]);

    const handlePriceChange = (id, field, value) => {
        setEditingRows(prev => ({
            ...prev,
            [id]: {
                ...(prev[id] || pricings.find(p => p.id === id)),
                [field]: value
            }
        }));
    };

    const handleSave = async (id) => {
        const edited = editingRows[id];
        if (!edited) return;

        setSavingId(id);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            
            if (partnerId) {
                // Save to partner-specific override
                await axios.post(`${backendUrl}/api/partner-product-pricing`, {
                    partner_id: partnerId,
                    product_pricing_id: id,
                    partner_price: edited.partner_price,
                    end_customer_price: edited.end_customer_price
                }, { headers: getAuthHeaders() });
            } else {
                // Save to global product pricing
                await axios.put(`${backendUrl}/api/product-pricing/${id}`, {
                    ...edited,
                    product_id: productId || edited.product_id
                }, { headers: getAuthHeaders() });
            }

            Swal.fire({ icon: 'success', title: 'Saved', timer: 1000, showConfirmButton: false });

            // Refresh data to get the new override IDs if any
            fetchProductDetails();
            setEditingRows(prev => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
        } catch (err) {
            console.error('Save error:', err);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update pricing.' });
        } finally {
            setSavingId(null);
        }
    };

    const handleDeleteOverride = async (overrideId) => {
        if (!overrideId) return;
        
        const result = await Swal.fire({
            title: 'Clear Override?',
            text: "This will revert to the global default pricing for this client.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, clear it!'
        });

        if (result.isConfirmed) {
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                await axios.delete(`${backendUrl}/api/partner-product-pricing/${overrideId}`, {
                    headers: getAuthHeaders()
                });
                Swal.fire({ icon: 'success', title: 'Cleared', timer: 1000, showConfirmButton: false });
                fetchProductDetails();
            } catch (err) {
                console.error('Delete override error:', err);
                Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to clear override.' });
            }
        }
    };

 const formatCurrency = (val) => {
    if (val === null || val === undefined || val === '') return '—';

    const number = Number(String(val).replace(/[^0-9.-]+/g, ''));
    
    return number.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};
    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-2 text-muted">Loading license pricing...</p>
            </div>
        );
    }

    // Extract unique options
    const uniqueProducts = [...new Set(pricings.map(p => p.product_name))].filter(Boolean);
    const uniqueLicenses = [...new Set(pricings.map(p => p.license_model))].filter(Boolean);

    // Apply filters
    const filteredPricings = pricings.filter(p => {
        const matchProduct = filterProduct ? p.product_name === filterProduct : true;
        const matchLicense = filterLicense ? p.license_model === filterLicense : true;
        return matchProduct && matchLicense;
    });

    return (
        <div className="license-pricing-container"><br/><br/>
           {!productId && (
               <div className="row mb-3 gx-2 align-items-center">
                   <div className="col-auto">
                       <label className="visually-hidden">Filter by Product</label>
                       <select className="form-select form-select-sm" value={filterProduct} onChange={(e) => setFilterProduct(e.target.value)}>
                           <option value="">All Products</option>
                           {uniqueProducts.map(prod => (
                               <option key={prod} value={prod}>{prod}</option>
                           ))}
                       </select>
                   </div>
                   <div className="col-auto">
                       <label className="visually-hidden">Filter by License Model</label>
                       <select className="form-select form-select-sm" value={filterLicense} onChange={(e) => setFilterLicense(e.target.value)}>
                           <option value="">All License Models</option>
                           {uniqueLicenses.map(lic => (
                               <option key={lic} value={lic}>{lic.replace(/_/g, ' ')}</option>
                           ))}
                       </select>
                   </div>
               </div>
           )}

            <div className="table-responsive bg-white rounded shadow-sm border">
                <table className="table table-hover mb-0 align-middle" style={{ fontSize: '13px' }}>
                    <thead className="bg-light">
                        <tr>
                            {!productId && <th className="px-3 py-3 text-muted fw-bold">Product</th>}
                            <th className="px-3 py-3 text-muted fw-bold">License Model</th>
                            <th className="py-3 text-primary fw-bold text-end pe-4" style={{ width: '160px' }}>
                                <FaGlobe className="me-1" />  Price
                            </th>
                            <th className="py-3 text-info fw-bold text-end pe-4" style={{ width: '160px' }}>
                                <FaHandshake className="me-1" /> Partner Price
                            </th>
                            <th className="py-3 text-success fw-bold text-end pe-4" style={{ width: '160px' }}>
                                <FaUserTie className="me-1" /> End Customer
                            </th>
                            <th className="py-3 text-center" style={{ width: '100px' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPricings.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="text-center py-5 text-muted">
                                    No license models found matching the filters.
                                </td>
                            </tr>
                        ) : (
                            filteredPricings.map((pricing) => {
                                const isEditing = editingRows[pricing.id] !== undefined;
                                const row = editingRows[pricing.id] || pricing;

                                return (
                                    <tr key={pricing.id}>
                                        {!productId && (
                                            <td className="px-3 py-3 fw-medium">
                                                {pricing.product_name}
                                            </td>
                                        )}
                                        <td className="px-3 py-3">
                                            <span className="badge bg-soft-primary text-primary text-capitalize px-2 py-1" style={{ fontSize: '11px' }}>
                                                {pricing.license_model?.replace(/_/g, ' ')}
                                            </span>
                                        </td>
                                      
                                        <td className="text-end pe-4">
                                            <div className="input-group input-group-sm justify-content-end">
                                                <span className="input-group-text bg-light border-end-0" style={{ fontSize: '11px' }}></span>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm text-end border-start-0"
                                                    style={{ maxWidth: '100px', fontWeight: '600', backgroundColor: partnerId ? '#f8f9fa' : 'white' }}
                                                    value={row.price || ''}
                                                    readOnly={!!partnerId}
                                                    onChange={(e) => handlePriceChange(pricing.id, 'price', e.target.value)}
                                                />
                                            </div>
                                        </td>
                                        <td className="text-end pe-4">
                                            <div className="input-group input-group-sm justify-content-end">
                                                <span className="input-group-text bg-light border-end-0" style={{ fontSize: '11px' }}></span>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm text-end border-start-0"
                                                    style={{ maxWidth: '100px', background: row.partner_price ? '#f0faff' : 'white' }}
                                                    placeholder={partnerId ? formatCurrency(row.global_partner_price || row.price) : formatCurrency(row.price)}
                                                    value={row.partner_price || ''}
                                                    onChange={(e) => handlePriceChange(pricing.id, 'partner_price', e.target.value)}
                                                />
                                            </div>
                                        </td>
                                        <td className="text-end pe-4">
                                            <div className="input-group input-group-sm justify-content-end">
                                                <span className="input-group-text bg-light border-end-0" style={{ fontSize: '11px' }}></span>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm text-end border-start-0"
                                                    style={{ maxWidth: '100px', background: row.end_customer_price ? '#f0fff4' : 'white' }}
                                                    placeholder={partnerId ? formatCurrency(row.global_end_customer_price || row.price) : formatCurrency(row.price)}
                                                    value={row.end_customer_price || ''}
                                                    onChange={(e) => handlePriceChange(pricing.id, 'end_customer_price', e.target.value)}
                                                />
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex justify-content-center gap-1">
                                                {isEditing ? (
                                                    <button
                                                        className="btn btn-primary btn-sm rounded-circle p-1"
                                                        onClick={() => handleSave(pricing.id)}
                                                        disabled={savingId === pricing.id}
                                                        title="Save changes"
                                                    >
                                                        {savingId === pricing.id ? (
                                                            <span className="spinner-border spinner-border-sm" role="status"></span>
                                                        ) : (
                                                            <FaSave size={14} />
                                                        )}
                                                    </button>
                                                ) : partnerId && pricing.override_id ? (
                                                    <button
                                                        className="btn btn-soft-danger btn-sm rounded-circle p-1"
                                                        onClick={() => handleDeleteOverride(pricing.override_id)}
                                                        title="Clear Override"
                                                    >
                                                        <BsX size={18} />
                                                    </button>
                                                ) : (
                                                    <span className="text-success small">
                                                        <i className="mdi mdi-check-circle-outline font-size-16"></i>
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

       
        </div>
    );
};

export default PartnerPricing;
